import {
  ChevronDown,
  Maximize2,
  Minus,
  Plus,
  RotateCcw,
  RotateCw,
} from "lucide-react";
import { memo } from "preact/compat";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import type {
  MapDocument,
  MapLocation,
  MapPin,
  MapViewOptions,
} from "../shared/types";
import {
  isMapLocationHidden,
  mapPreviewOpacity,
  previewHiddenMapDocument,
  visibleMapDocument,
} from "../shared/mapPresentation";
import { VenueMapScene } from "./VenueMapScene";
import {
  VenueMapWebGLScene,
  type WebGLMapStatus,
  type WebGLMapFailure,
} from "./VenueMapWebGLScene";
import { VenueMapPins, type VenueMapPinGroup } from "./VenueMapPins";
import { VenueMapSearch } from "./VenueMapSearch";
import { VenueMapSelection } from "./VenueMapSelection";
import { useVenueMapInteraction } from "./useVenueMapInteraction";
import { useVenueMapRoute } from "./useVenueMapRoute";
import type { RoutingFailure } from "./venueMapRoutingWorkerProtocol";
import { mapLocationAtPoint } from "./mapHitTest";
import styles from "./event-map.css?inline";

import type {
  EventMapHandle,
  MapViewState,
  MapRouteChange,
} from "../shared/mapControls";
import { applyRouteAppearance } from "./venueMapAppearance";
import { useVenueMapLogos, type VenueMapLogos } from "./useVenueMapLogos";
export type { EventMapHandle } from "../shared/mapControls";
export interface EventMapProps extends MapViewOptions {
  document: MapDocument;
  styleNonce?: string;
  logoBaseUrl?: string;
  /** Optional shared assets; the supplying component owns their lifetime. */
  logos?: VenueMapLogos;
  onReady?: (handle: EventMapHandle) => void;
  onError?: (error: Error) => void;
  onRendererFailure?: (reason: WebGLMapFailure) => void;
  onRoutingFailure?: (reason: RoutingFailure) => void;
  onViewChange?: (view: MapViewState) => void;
  onRouteChange?: (change: MapRouteChange) => void;
  /** Editor-only preview; never changes saved visibility or routing availability. */
  showHidden?: boolean;
  /** Editor action; public embeds do not expose visibility controls. */
  onToggleVisibility?: (location: MapLocation) => void;
  visibilityDisabled?: boolean;
  /** User dismissal only; controller changes do not notify the host. */
  onSelectionClear?: () => void;
}
const EMPTY_PINS: MapPin[] = [];
const MemoScene = memo(
  VenueMapScene,
  (a, b) =>
    a.document === b.document &&
    a.description === b.description &&
    a.route === b.route &&
    a.selectedStandId === b.selectedStandId &&
    a.logos === b.logos &&
    Object.keys(a.camera).every(
      (key) =>
        a.camera[key as keyof typeof a.camera] ===
        b.camera[key as keyof typeof b.camera],
    ) &&
    Object.keys(a.viewportBounds).every(
      (key) =>
        a.viewportBounds[key as keyof typeof a.viewportBounds] ===
        b.viewportBounds[key as keyof typeof b.viewportBounds],
    ),
);

/** A self-contained map. Each instance owns its geometry, camera, GPU resources and gestures. */
export function EventMap({
  document: sourceMap,
  pins = EMPTY_PINS,
  route: requestedRoute,
  routeStyle,
  controls = true,
  onSelect,
  onSelectionClear,
  showHidden = false,
  onToggleVisibility,
  visibilityDisabled,
  onReady,
  onRendererFailure,
  onRoutingFailure,
  onViewChange,
  onRouteChange,
  styleNonce,
  logoBaseUrl,
  logos: suppliedLogos,
}: EventMapProps) {
  const publicMap = useMemo(() => visibleMapDocument(sourceMap), [sourceMap]);
  const visibleMap = useMemo(
    () => (showHidden ? previewHiddenMapDocument(sourceMap) : publicMap),
    [sourceMap, publicMap, showHidden],
  );
  const logos = useVenueMapLogos(visibleMap, logoBaseUrl, suppliedLogos);
  const [webglStatus, setWebGLStatus] = useState<WebGLMapStatus>("loading");
  const [controlsOpen, setControlsOpen] = useState(false);
  const [compactControls, setCompactControls] = useState(false);
  const frame = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const element = frame.current;
    if (!element) return;
    // Measure the outer frame, whose height is unaffected by whether the
    // toolbar is floating or in the layout. Using the viewport would oscillate
    // between modes as the toolbar consumes/relinquishes space.
    const measure = () => {
      const { width, height } = element.getBoundingClientRect();
      setCompactControls(width <= 700 || height < 400);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const [selected, setSelected] = useState<MapLocation | null>(null);
  const selection = useRef<HTMLElement>(null);
  const [showLoading, setShowLoading] = useState(false);
  useEffect(() => {
    if (webglStatus !== "loading") {
      setShowLoading(false);
      return;
    }
    // Avoid flashing a loading card during quick renderer/atlas updates.
    const timeout = setTimeout(() => setShowLoading(true), 250);
    return () => clearTimeout(timeout);
  }, [webglStatus]);
  const controlsId = useId();
  const controlsToggle = useRef<HTMLButtonElement>(null);
  const locations = useMemo(
    () =>
      new Map(visibleMap.locations.map((location) => [location.id, location])),
    [visibleMap.locations],
  );
  const pinById = useMemo(
    () => new Map(pins.map((pin) => [pin.id, pin])),
    [pins],
  );
  const pinGroups = useMemo(() => {
    const groups = new Map<string, VenueMapPinGroup>();
    pins.forEach((pin, index) => {
      const location = locations.get(pin.locationId);
      if (!location) return;
      let group = groups.get(location.id);
      if (!group) {
        group = {
          ...location,
          opacity: mapPreviewOpacity(location),
          stops: [],
        };
        groups.set(location.id, group);
      }
      group.stops.push({
        id: pin.id,
        number: pin.number ?? index + 1,
        title: pin.label || location.label,
        hasPlayed: !!pin.completed,
        isNext: requestedRoute?.to === location.id,
      });
    });
    return [...groups.values()];
  }, [pins, locations, requestedRoute?.to]);
  const routingLocations = useMemo(
    () =>
      new Map(publicMap.locations.map((location) => [location.id, location])),
    [publicMap.locations],
  );
  const routing = useVenueMapRoute(
    sourceMap,
    requestedRoute,
    routingLocations,
    onRoutingFailure,
  );
  const route = routing.route;
  const activeRoute = useMemo(
    () => (route ? { from: route.fromStopId, to: route.toStopId } : null),
    [route],
  );
  const map = useMemo(
    () => applyRouteAppearance(visibleMap, activeRoute, routeStyle),
    [
      visibleMap,
      activeRoute,
      routeStyle?.contextSaturation,
      routeStyle?.endpointLift,
    ],
  );
  const hitDocument = useMemo(
    () =>
      applyRouteAppearance(
        showHidden ? visibleMap : sourceMap,
        activeRoute,
        routeStyle,
      ),
    [
      sourceMap,
      visibleMap,
      showHidden,
      activeRoute,
      routeStyle?.contextSaturation,
      routeStyle?.endpointLift,
    ],
  );
  const interaction = useVenueMapInteraction(map, pinGroups);
  const {
    viewport,
    viewportBounds,
    zoom,
    interacting,
    changeZoom,
    changePitch,
    rotate,
    fit,
    focus,
  } = interaction;
  const {
    imageWidth,
    imageHeight,
    sourceWidth,
    sourceHeight,
    sourceElevation,
    stageWidth,
    stageHeight,
    offsetX,
    offsetY,
    panX,
    panY,
    rotation,
    pitch,
    perspective,
  } = interaction;
  const camera = {
    imageWidth,
    imageHeight,
    sourceWidth,
    sourceHeight,
    sourceElevation,
    stageWidth,
    stageHeight,
    offsetX,
    offsetY,
    panX,
    panY,
    rotation,
    pitch,
    perspective,
  };
  const currentRouting = useRef(routing);
  currentRouting.current = routing;
  const queuedRouteFit = useRef<typeof routing.request>(null);
  const selectionMapId = useRef(map.id);
  const currentSelection = useRef(selected);
  currentSelection.current = selected;
  const clearSelection = useCallback(() => {
    // Selection can also be cleared through the host API. Only restore focus
    // when the removed card owns it; never move focus away from host controls.
    if (selection.current?.matches(":focus-within"))
      viewport.current?.focus({ preventScroll: true });
    setSelected(null);
  }, []);
  const controller = useRef(interaction);
  controller.current = interaction;
  const locationRef = useRef(locations);
  locationRef.current = locations;
  const handle = useMemo<EventMapHandle>(
    () => ({
      fit: () => controller.current.fit(),
      fitRoute: () => {
        const current = currentRouting.current;
        queuedRouteFit.current =
          current.status === "pending" ? current.request : null;
        if (current.route)
          controller.current.fitRoute(
            current.route,
            currentSelection.current ? 144 : 0,
          );
      },
      reset: () => controller.current.reset(),
      zoomIn: () =>
        controller.current.changeZoom(controller.current.getView().zoom + 0.5),
      zoomOut: () =>
        controller.current.changeZoom(controller.current.getView().zoom - 0.5),
      setZoom: (value) => controller.current.changeZoom(value),
      rotate: (degrees) => controller.current.rotate(degrees),
      setRotation: (degrees) => controller.current.changeRotation(degrees),
      setPitch: (degrees) => controller.current.changePitch(degrees),
      focusLocation: (id) => {
        const location = locationRef.current.get(id);
        if (!location)
          throw new Error("This location is not available on the map.");
        setSelected(location);
        setControlsOpen(false);
        controller.current.focus(location);
      },
      clearSelection,
      getView: () => controller.current.getView(),
    }),
    [],
  );
  useLayoutEffect(() => {
    if (
      webglStatus !== "loading" &&
      viewportBounds.width > 0 &&
      viewportBounds.height > 0
    )
      onReady?.(handle);
  }, [
    handle,
    onReady,
    webglStatus,
    viewportBounds.width,
    viewportBounds.height,
  ]);
  useLayoutEffect(() => {
    if (viewportBounds.width > 0 && viewportBounds.height > 0)
      onViewChange?.({ zoom, rotation, pitch });
  }, [
    zoom,
    rotation,
    pitch,
    viewportBounds.width,
    viewportBounds.height,
    onViewChange,
  ]);
  useLayoutEffect(() => {
    if (queuedRouteFit.current !== routing.request)
      queuedRouteFit.current = null;
    if (routing.status === "pending") return;
    if (queuedRouteFit.current && route) {
      queuedRouteFit.current = null;
      controller.current.fitRoute(route, currentSelection.current ? 144 : 0);
    } else queuedRouteFit.current = null;
    onRouteChange?.({
      route: requestedRoute
        ? { from: requestedRoute.from, to: requestedRoute.to }
        : null,
      status: routing.status,
    });
  }, [requestedRoute?.from, requestedRoute?.to, routing, route, onRouteChange]);
  useLayoutEffect(() => {
    if (selectionMapId.current !== map.id) {
      selectionMapId.current = map.id;
      clearSelection();
      return;
    }
    const previous = currentSelection.current;
    if (!previous) return;
    const stand = visibleMap.geometry.stands.find(
      (item) => item.id === previous.id,
    );
    // Booth picking also supports imported booths without a navigation entry.
    // Keep those editor selections while editing/hiding a ghost; the external
    // focus API still requires a real location via locationRef above.
    const boothOnly =
      stand && !sourceMap.locations.some((item) => item.id === stand.id)
        ? {
            id: stand.id,
            label: [stand.id, stand.name].filter(Boolean).join(" · "),
            x: stand.label.x / visibleMap.geometry.width,
            y: stand.label.y / visibleMap.geometry.height,
          }
        : undefined;
    const next =
      showHidden || !isMapLocationHidden(sourceMap, previous.id)
        ? (locations.get(previous.id) ?? boothOnly)
        : undefined;
    if (next) setSelected(next);
    else clearSelection();
  }, [map.id, sourceMap, visibleMap, locations, showHidden, clearSelection]);
  const onStatus = useCallback(
    (status: WebGLMapStatus) => setWebGLStatus(status),
    [],
  );
  const select = (location: MapLocation, pin?: MapPin) => {
    setSelected(location);
    onSelect?.(location, pin);
  };
  return (
    <div
      className="event-map-root"
      data-compact-controls={compactControls}
      style={{ "--event-map-viewport-height": `${viewportBounds.height}px` }}
    >
      <style nonce={styleNonce}>{styles}</style>
      <figure
        ref={frame}
        className="venue-map"
        data-map-id={map.id}
        data-show-hidden={showHidden}
        data-route-status={routing.status}
        data-map-mode="3d"
        data-map-renderer={
          webglStatus === "ready"
            ? "webgl"
            : webglStatus === "failed"
              ? "svg"
              : "loading"
        }
        data-controls-open={controlsOpen}
        onKeyDown={(event) => {
          if (
            event.key === "Escape" &&
            controlsOpen &&
            controlsToggle.current?.getClientRects().length
          ) {
            event.preventDefault();
            setControlsOpen(false);
            controlsToggle.current?.focus();
          }
        }}
      >
        {controls && (
          <>
            <button
              ref={controlsToggle}
              type="button"
              className="venue-map-controls-toggle"
              aria-label={
                controlsOpen ? "Hide map controls" : "Show map controls"
              }
              aria-expanded={controlsOpen}
              aria-controls={controlsId}
              onClick={() => setControlsOpen((open) => !open)}
            >
              <ChevronDown size={20} aria-hidden="true" />
            </button>
            {compactControls && (
              <VenueMapSearch
                compact
                document={map}
                selectedId={selected?.id}
                onOpen={() => setControlsOpen(false)}
                onSelect={(location) => {
                  select(location);
                  focus(location);
                  viewport.current?.focus({ preventScroll: true });
                }}
              />
            )}
            <div id={controlsId} className="venue-map-toolbar">
              {!compactControls && (
                <VenueMapSearch
                  document={map}
                  selectedId={selected?.id}
                  onSelect={(location) => {
                    select(location);
                    focus(location);
                    viewport.current?.focus({ preventScroll: true });
                  }}
                />
              )}
              <div
                className="venue-map-controls"
                role="group"
                aria-label="Zoom"
              >
                <button
                  type="button"
                  onClick={() => changeZoom(zoom - 0.5)}
                  disabled={zoom === 1}
                  aria-label="Zoom out"
                  title="Zoom out"
                >
                  <Minus size={18} aria-hidden="true" />
                </button>
                <output
                  aria-live={interacting ? "off" : "polite"}
                  aria-label="Zoom"
                  dir="ltr"
                >
                  {Math.round(zoom * 100)}%
                </output>
                <button
                  type="button"
                  onClick={() => changeZoom(zoom + 0.5)}
                  disabled={zoom === 4}
                  aria-label="Zoom in"
                  title="Zoom in"
                >
                  <Plus size={18} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="venue-map-fit"
                  onClick={fit}
                  aria-label="Fit map"
                  title="Fit map"
                >
                  <Maximize2 size={16} aria-hidden="true" />
                  <span>Fit map</span>
                </button>
              </div>
              <div
                className="venue-map-controls venue-map-rotation-controls"
                role="group"
                aria-label="Rotation"
                dir="ltr"
              >
                <button
                  type="button"
                  onClick={() => rotate(-15)}
                  aria-label="Rotate left"
                  title="Rotate left · Shift + drag to rotate"
                >
                  <RotateCcw size={18} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => rotate(15)}
                  aria-label="Rotate right"
                  title="Rotate right · Shift + drag to rotate"
                >
                  <RotateCw size={18} aria-hidden="true" />
                </button>
              </div>
              <div className="venue-map-angle-controls">
                <div className="venue-map-angle-slider" dir="ltr">
                  <button
                    type="button"
                    className="venue-map-angle-preset"
                    onClick={() => changePitch(0)}
                    aria-pressed={pitch === 0}
                  >
                    Overhead
                  </button>
                  <input
                    type="range"
                    min={0}
                    max={70}
                    step={1}
                    value={pitch}
                    aria-label="Viewing angle"
                    aria-valuetext={`${Math.round(pitch)} degrees`}
                    title="Shift + drag vertically to tilt"
                    onInput={(event) =>
                      changePitch(Number(event.currentTarget.value))
                    }
                  />
                  <button
                    type="button"
                    className="venue-map-angle-preset"
                    onClick={() => changePitch(70)}
                    aria-pressed={pitch === 70}
                  >
                    Low angle
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
        <div
          ref={viewport}
          className={`venue-map-viewport${interacting ? " is-interacting" : ""}`}
          role="region"
          aria-busy={webglStatus === "loading"}
          aria-label={`${map.title}. Drag to pan, pinch to zoom, or Shift + drag to rotate and tilt. Arrow keys pan; plus and minus zoom; Home fits the map.`}
          tabIndex={0}
          dir="ltr"
          onClick={(event) => {
            if (
              event.defaultPrevented ||
              event.button !== 0 ||
              event.shiftKey ||
              event.ctrlKey ||
              event.metaKey ||
              event.altKey ||
              event.detail > 1
            )
              return;
            const bounds = event.currentTarget.getBoundingClientRect();
            const location = mapLocationAtPoint(hitDocument, camera, {
              x: event.clientX - bounds.left + event.currentTarget.scrollLeft,
              y: event.clientY - bounds.top + event.currentTarget.scrollTop,
            });
            if (location) select(location);
          }}
        >
          <div
            className="venue-map-stage"
            style={{ width: stageWidth, height: stageHeight }}
          >
            {webglStatus === "failed" ? (
              <MemoScene
                document={map}
                camera={camera}
                viewportBounds={viewportBounds}
                description={map.title}
                route={route}
                selectedStandId={selected?.id}
                logos={logos}
              />
            ) : (
              <VenueMapWebGLScene
                document={map}
                camera={camera}
                viewportBounds={viewportBounds}
                description={map.title}
                route={route}
                selectedStandId={selected?.id}
                logos={logos}
                onStatus={onStatus}
                onFailure={onRendererFailure}
              />
            )}
            <VenueMapPins
              camera={camera}
              geometry={map.geometry}
              groups={pinGroups}
              onNavigate={(id) => {
                const pin = pinById.get(id);
                const location = pin && locations.get(pin.locationId);
                if (location) select(location, pin);
              }}
            />
          </div>
        </div>
        <div className="venue-map-feedback" role="status" aria-live="polite">
          {showLoading && webglStatus === "loading" && (
            <span className="venue-map-loading">Preparing map…</span>
          )}
          <span className="venue-map-sr-only">
            {selected ? `${selected.label} selected` : ""}
          </span>
        </div>
        {selected && (
          <VenueMapSelection
            elementRef={selection}
            location={selected}
            logos={logos}
            stand={map.geometry.stands.find(
              (stand) => stand.id === selected.id,
            )}
            hidden={
              sourceMap.geometry.stands.some(
                (stand) => stand.id === selected.id,
              )
                ? sourceMap.geometry.stands.some(
                    (stand) => stand.id === selected.id && stand.hidden,
                  )
                : !!sourceMap.locations.find(
                    (location) => location.id === selected.id,
                  )?.hidden
            }
            visibilityDisabled={visibilityDisabled}
            onToggleVisibility={
              onToggleVisibility
                ? () => onToggleVisibility(selected)
                : undefined
            }
            onFocus={() => focus(selected)}
            onDismiss={() => {
              viewport.current?.focus({ preventScroll: true });
              clearSelection();
              onSelectionClear?.();
            }}
          />
        )}
      </figure>
    </div>
  );
}
